import { ComponentProps, Fragment, ReactNode } from "react";
import { ArrowLeftRight, LucideIcon, ReceiptEuro } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/shared/components/ui/sidebar";
import { cn } from "@/shared/lib/utils";
import { Separator } from "@/shared/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";

/**
 * InvoicesPage is the parent node used to build the default process page.
 * The content should be composed using the other components in this file.
 */
export function InvoicesPage({
  children,
  defaultOpen,
  ...props
}: ComponentProps<typeof SidebarProvider>) {
  return (
    <SidebarProvider
      defaultOpen={
        defaultOpen !== undefined
          ? defaultOpen
          : window.matchMedia("(min-width: 1280px)").matches
      }
      {...props}
    >
      {children}
    </SidebarProvider>
  );
}

export type NavigationGroup = {
  label: string;
  items: NavigationItem[];
};

export type NavigationItem = {
  label: string;
  icon: LucideIcon;
  to: string;
  children?: NavigationItem[];
};

export function InvoicesPageSidebar({
  activePath,
  onNavigate,
}: {
  activePath: string;
  onNavigate: (to: string) => void;
}) {
  const groups: NavigationGroup[] = [
    {
      label: "Business Central",
      items: [
        {
          label: "Übermittlungen",
          icon: ArrowLeftRight,
          to: "/invoices/business-central/outbox",
          children: [
            {
              label: "Gesec Rechnungen",
              icon: ReceiptEuro,
              to: "/invoices/business-central/outbox/gesec-invoices",
            },
          ],
        },
      ],
    },
  ];

  return (
    <Sidebar collapsible="icon" className="pt-16">
      <SidebarContent>
        {groups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      isActive={activePath.startsWith(item.to)}
                      onClick={() => onNavigate(item.to)}
                    >
                      <item.icon className="mr-2 h-5 w-5" />
                      {item.label}
                    </SidebarMenuButton>
                    {item.children && (
                      <SidebarMenuSub>
                        {item.children.map((subItem) => (
                          <SidebarMenuSubItem key={subItem.to}>
                            <SidebarMenuSubButton
                              isActive={activePath.startsWith(subItem.to)}
                              onClick={() => onNavigate(subItem.to)}
                            >
                              <subItem.icon className="mr-2 h-5 w-5" />
                              {subItem.label}
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    )}
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
}

/**
 * InvoicesPageMain is the parent node for the main content of an
 * invoices page.
 */
export function InvoicesPageMain({
  children,
  className,
  ...props
}: ComponentProps<typeof SidebarInset>) {
  return (
    <SidebarInset className={cn("pt-16", className)} {...props}>
      {children}
    </SidebarInset>
  );
}

export type InvoicesPageBreadcrumb = {
  label: string;
  to?: string;
};

/**
 * InvoicesPageMainHeader defines a header for the main page. The header
 * will stick and always be visible. It should only contain information and
 * actions that are useful to the user at all times. E.g. Breadcrumbs including
 * information on the process customer, process number, document etc.
 * By convention the InvoicesPageMainHeader should include the
 * InvoicesPageSidebarTrigger.
 *
 * Be aware of the shirking height when the sidebar is collapsed.
 */
export function InvoicesPageMainHeader({
  breadcrumbs,
  onNavigateTo: navigateTo,
  children,
  className,
}: {
  breadcrumbs: InvoicesPageBreadcrumb[];
  onNavigateTo: (to: string) => void;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={cn(
        // The w-full will extend the header over the screen. To compensate,
        // the pr is dynamically calculated based on the sidebar width.
        "bg-background fixed flex h-12 w-full shrink-0 items-center gap-2 border-b px-4 pr-[calc(var(--sidebar-width)+(--spacing(4)))] transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 group-has-data-[collapsible=icon]/sidebar-wrapper:pr-[calc(var(--sidebar-width-icon)+(--spacing(4)))]",
        className,
      )}
    >
      <div className="flex w-full items-center gap-2">
        <SidebarTrigger />
        <Separator orientation="vertical" className="mr-2 min-h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbs?.map((breadcrumb, index) => (
              <Fragment key={breadcrumb.label}>
                <BreadcrumbItem>
                  {breadcrumb.to ? (
                    <BreadcrumbLink onClick={() => navigateTo(breadcrumb.to!)}>
                      {breadcrumb.label}
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbPage>{breadcrumb.label}</BreadcrumbPage>
                  )}
                </BreadcrumbItem>
                {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
              </Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
        <div className="ml-auto">{children}</div>
      </div>
    </header>
  );
}

export function InvoicesPageMainContent({ children }: { children: ReactNode }) {
  return (
    <div className="mt-12 flex flex-1 flex-col gap-4 p-4 pl-5 group-has-data-[collapsible=icon]/sidebar-wrapper:mt-12">
      {children}
    </div>
  );
}
